import emailjs from '@emailjs/browser'
import L, { LatLngExpression } from 'leaflet'
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'
import React, { FormEvent, useState } from 'react'
import { MapContainer, Marker, TileLayer } from 'react-leaflet'
import {
  emailjsPublicKey,
  emailjsServiceId,
  emailjsTemplateId,
} from '@/services/environment'
import {
  getFieldError,
  sanitizeInput,
  validateEmail,
  validateMessage,
  validateName,
  validateSubject,
} from '@/utils/validation'
import type { ValidationError } from '@/types/validationTypes'
import './index.scss'

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [errors, setErrors] = useState<ValidationError[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const position: LatLngExpression = [43.65107, -79.347015]
  const defaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
  })

  const validateField = (field: string, value: string) => {
    let error: string | null = null

    switch (field) {
      case 'name':
        error = validateName(value)
        break
      case 'email':
        error = validateEmail(value)
        break
      case 'subject':
        error = validateSubject(value)
        break
      case 'message':
        error = validateMessage(value)
        break
    }

    if (error) {
      setErrors((prev) => {
        const filtered = prev.filter((e) => e.field !== field)
        return [...filtered, { field, message: error! }]
      })
    } else {
      setErrors((prev) => prev.filter((e) => e.field !== field))
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors.some((err) => err.field === name)) {
      validateField(name, value)
    }
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    validateField(name, value)
  }

  const sendEmail = async (e: FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Validate all fields
    const validationErrors: ValidationError[] = []
    const nameError = validateName(formData.name)
    const emailError = validateEmail(formData.email)
    const subjectError = validateSubject(formData.subject)
    const messageError = validateMessage(formData.message)

    if (nameError) validationErrors.push({ field: 'name', message: nameError })
    if (emailError)
      validationErrors.push({ field: 'email', message: emailError })
    if (subjectError)
      validationErrors.push({ field: 'subject', message: subjectError })
    if (messageError)
      validationErrors.push({ field: 'message', message: messageError })

    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      setIsSubmitting(false)
      return
    }

    try {
      // Sanitize inputs before sending
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        subject: sanitizeInput(formData.subject),
        message: sanitizeInput(formData.message),
      }

      // Create a temporary form element for EmailJS
      const formElement = document.createElement('form')
      formElement.innerHTML = `
        <input type="text" name="name" value="${sanitizedData.name}" />
        <input type="email" name="email" value="${sanitizedData.email}" />
        <input type="text" name="subject" value="${sanitizedData.subject}" />
        <textarea name="message">${sanitizedData.message}</textarea>
      `

      await emailjs.sendForm(
        emailjsServiceId,
        emailjsTemplateId,
        formElement,
        emailjsPublicKey
      )

      window.alert('Message sent successfully!')
      setFormData({ name: '', email: '', subject: '', message: '' })
      setErrors([])
    } catch (error) {
      window.alert('Message failed to send.')
      console.error('EmailJS error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex m-auto contact-page">
      <div className="text-zone m-auto p-4 me-0 flex flex-col items-center justify-center max-w-[560px] max-h-[90%]">
        <h1 className="text-[53px] font-coolvetica text-primary font-normal mt-0 relative mb-1.5">
          Contact Me
        </h1>
        <p className="text-[16px] leading-[2] text-white font-sans max-w-fit animate-pulseOnce">
          I am interested in all things coding related - especially
          architectural design, web development, and data science. If you have
          any questions or would like to work together, don't hesitate to reach
          out!
        </p>
        <div className="contact-form w-full mt-5">
          <form onSubmit={sendEmail}>
            <ul className="p-0 m-0">
              <li
                className="half
	      w-[49%]
	      ml-[2%]
	      float-left
	      clear-none
	      mb-2.5
	      opacity-0
	      overflow-hidden
	      relative
	      animate-fadeInUp
	      animation-delay-[2s]
	      animation-fill-mode-forwards"
              >
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full
		  border-0
		  bg-[#115173]
		  h-[50px]
		  text-[16px]
		  text-white
		  p-5
		  box-border"
                />
                {getFieldError(errors, 'name') && (
                  <span className="text-red-400 text-sm mt-1 block">
                    {getFieldError(errors, 'name')}
                  </span>
                )}
              </li>
              <li
                className="half
	      w-[49%]
	      ml-[2%]
	      float-left
	      clear-none
	      mb-2.5
	      opacity-0
	      overflow-hidden
	      relative
	      animate-fadeInUp
	      animation-delay-[2s]
	      animation-fill-mode-forwards"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full
		  border-0
		  bg-[#115173]
		  h-[50px]
		  text-[16px]
		  text-white
		  p-5
		  box-border"
                />
                {getFieldError(errors, 'email') && (
                  <span className="text-red-400 text-sm mt-1 block">
                    {getFieldError(errors, 'email')}
                  </span>
                )}
              </li>
              <li
                className="mb-2.5
	      opacity-0
	      overflow-hidden
	      relative
	      animate-fadeInUp
	      animation-delay-[2s]
	      animation-fill-mode-forwards"
              >
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full
		  border-0
		  bg-[#115173]
		  h-[50px]
		  text-[16px]
		  text-white
		  p-5
		  box-border"
                />
                {getFieldError(errors, 'subject') && (
                  <span className="text-red-400 text-sm mt-1 block">
                    {getFieldError(errors, 'subject')}
                  </span>
                )}
              </li>
              <li
                className="mb-2.5
	      opacity-0
	      overflow-hidden
	      relative
	      animate-fadeInUp
	      animation-delay-[2s]
	      animation-fill-mode-forwards"
              >
                <textarea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full
		  border-0
		  bg-[#115173]
		  text-[16px]
		  text-white
		  p-8
		  box-border
		  min-h-[150px]"
                />
                {getFieldError(errors, 'message') && (
                  <span className="text-red-400 text-sm mt-1 block">
                    {getFieldError(errors, 'message')}
                  </span>
                )}
              </li>
              <li
                className="float-right
	      mb-2.5
	      opacity-0
	      overflow-hidden
	      relative
	      animate-fadeInUp
	      animation-delay-[2s]
	      animation-fill-mode-forwards"
              >
                <input
                  type="submit"
                  value={isSubmitting ? 'SENDING...' : 'SEND'}
                  disabled={isSubmitting}
                  className="
		  bg-transparent
		  text-primary
		  text-2xl
		  font-normal
		  tracking-wider
		  font-sans
		  no-underline
		  py-4
		  px-8
		  border
		  border-primary
		  mt-6
		  cursor-pointer
		  disabled:opacity-50
		  disabled:cursor-not-allowed
		  "
                />
              </li>
            </ul>
          </form>
        </div>
      </div>
      <div
        className="map-wrap
      bg-[rgba(8,253,216,0.1)]
      float-right
      w-[412px]
      h-[312px]
      mt-[6%]
      m-auto"
      >
        <div
          className="info-map
	bg-black
	z-[999999]
	w-[70%]
	h-[100px]
	p-5
	text-white
	font-['Helvetica']
	text-[16px]
	animate-fadeIn
	animation-delay-[1.5s]
	animation-fill-mode-forwards"
        >
          Luis Vargas,
          <br />
          Canada,
          <br />
          Toronto, ON
          <br />
          <br />
        </div>
        <MapContainer
          center={position}
          zoom={13}
          className="leaflet-container
	  relative
	  w-full
	  h-full
	  opacity-0
	  animate-backInRight
	  animation-delay-[1.2s]
	  animation-fill-mode-forwards"
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Marker position={position} icon={defaultIcon}></Marker>
        </MapContainer>
      </div>
    </div>
  )
}

export default Contact
